import React, { useState, useEffect } from "react";
import {
  FaHome,
  FaUserEdit,
  FaCertificate,
  FaTicketAlt,
  FaSignOutAlt,
  FaChevronLeft,
  FaBars,
  FaUsers,
  FaChartBar,
  FaClipboardList,
} from "react-icons/fa";
import { Routes, Route, NavLink } from "react-router-dom";
import Cookies from "js-cookie";

import ProfileCard from "../Profile/Profile_Card";
import ProfileEdit from "../Profile/Profile_Edit";
import CertificateSection from "./Certificates";
import EventBookings from "./EventBookings";
import StudentData from "./StudentsData"; // Importing the StudentData component
import Reports from "./Report";
import Requests from "./Requests";
import StudentAllocation from "./Allocate_Facilitators";

const Backdrop = ({ isOpen, onClose }) => (
  <div
    className={`dashboard-backdrop ${isOpen ? "visible" : ""}`}
    onClick={onClose}
  />
);

const SidebarItem = ({ to, icon: Icon, label, onClick, end = false }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
  >
    <Icon className="sidebar-icon" />
    <span className="sidebar-label">{label}</span>
    {/* Active Glow Indicator */}
    <div className="active-glow" />
  </NavLink>
);

const Sidebar = ({ isOpen, onClose, onLogout }) => {
  // 1. Get the role from cookies
  const userRole = Cookies.get("role");

  // 2. Define allowed roles for the new view
  const allowedRoles = ["facilitator", "area_leader", "mentor", "admin"];
  const showStudentData = allowedRoles.includes(userRole);
  const showreporttofacilitator = ["facilitator"].includes(userRole);
  const showMentorsData = ["admin", "area_leader"].includes(userRole);

  return (
    <aside className={`dashboard-sidebar ${isOpen ? "open" : "closed"}`}>
      <div className="sidebar-header">
        <h2 className="brand-logo">
          Dash<span className="highlight">Board</span>
        </h2>
        {/* Back Button to Hide Sidebar */}
        <button
          className="toggle-sidebar-btn"
          onClick={onClose}
          aria-label="Hide Sidebar"
        >
          <FaChevronLeft />
        </button>
      </div>

      <nav className="sidebar-nav">
        <div className="nav-group">
          <p className="nav-group-title">Menu</p>
          <SidebarItem to="/dashboard" icon={FaHome} label="Overview" end />
          <SidebarItem
            to="/dashboard/profileEdit"
            icon={FaUserEdit}
            label="Edit Profile"
          />
          <SidebarItem
            to="/dashboard/events-booked"
            icon={FaTicketAlt}
            label="My Bookings"
          />
          <SidebarItem
            to="/dashboard/certificates"
            icon={FaCertificate}
            label="Certificates"
          />

          {/* 3. Conditionally Render Sidebar Item */}
          {showStudentData && (
            <>
              <SidebarItem
                to="/dashboard/student-data"
                icon={FaUsers}
                label="Student Data"
              />
            </>
          )}
          {showreporttofacilitator && (
            <>
              <SidebarItem
                to="/dashboard/reports"
                icon={FaChartBar}
                label="Reports"
              />
            </>
          )}
          {showMentorsData && (
            <>
              <SidebarItem
                to="/dashboard/requests"
                icon={FaClipboardList}
                label="Requests"
              />
              <SidebarItem
                to="/dashboard/allocate-students"
                icon={FaClipboardList}
                label="Allocate Students"
              />
            </>
          )}
        </div>
      </nav>

      <div className="sidebar-footer">
        <button className="logout-btn" onClick={onLogout}>
          <FaSignOutAlt className="logout-icon" />
          <span className="logout-text">Log Out</span>
        </button>
      </div>
    </aside>
  );
};

export default function DashboardLayout() {
  // Default open on desktop, closed on mobile
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth > 1024);

  // 4. Check role in parent component to conditionally render Route
  const userRole = Cookies.get("role");
  const allowedRoles = ["facilitator", "mentor", "admin"];
  const showStudentData = allowedRoles.includes(userRole);
  const showreporttofacilitator = ["facilitator"].includes(userRole);
  const showMentorsData = ["admin", "area_leader"].includes(userRole);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) setSidebarOpen(false);
      else setSidebarOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    // Clear all potential auth cookies
    Cookies.remove("email", { path: "/" });
    Cookies.remove("role", { path: "/" });
    Cookies.remove("ec_auth_token", { path: "/" });
    window.location.href = "/Login";
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile Backdrop */}
      <Backdrop
        isOpen={isSidebarOpen && window.innerWidth <= 1024}
        onClose={() => setSidebarOpen(false)}
      />

      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
      />

      <main className={`dashboard-main ${isSidebarOpen ? "shifted" : ""}`}>
        {/* Top Header / Navbar Area within Dashboard */}
        <header className="dashboard-topbar">
          {/* Button to Show Sidebar if Hidden */}
          <button
            className={`open-sidebar-btn ${!isSidebarOpen ? "visible" : ""}`}
            onClick={() => setSidebarOpen(true)}
          >
            <FaBars />
          </button>
        </header>

        <div className="dashboard-content-wrapper">
          <Routes>
            <Route index element={<ProfileCard />} />
            <Route path="profileEdit" element={<ProfileEdit />} />
            <Route path="certificates" element={<CertificateSection />} />
            <Route path="events-booked" element={<EventBookings />} />

            {/* 5. Conditionally Render the Route */}
            {showStudentData && (
              <>
                <Route path="student-data" element={<StudentData />} />
              </>
            )}
            {showreporttofacilitator && (
              <>
                <Route path="reports" element={<Reports />} />
              </>
            )}
            {showMentorsData && (
              <>
                <Route path="requests" element={<Requests />} />
                <Route
                  path="allocate-students"
                  element={<StudentAllocation />}
                />
              </>
            )}
          </Routes>
        </div>
      </main>
    </div>
  );
}
